/* biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: existing error */
import cx from 'classnames';
import omit from 'lodash/omit';
import {useCallback, useContext, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {CSSTransition} from 'react-transition-group';
import {useCurrentRoute} from '~/CreateFabricInvitation/navigation';
import {isInStep4RefreshVariation} from '~/CreateFabricInvitation/utils/experiments';
import {Canvas} from '~/common/canvas/Canvas';
import {
  canvasAddTextBordersAndIcons,
  canvasHideEditIcon,
  canvasHideTextBorder,
  canvasRemoveEditIcon,
  canvasRemoveTextBordersAndIcons,
  canvasShowTextBordersAndIcons,
} from '~/common/canvas/customs/text_border';
import {CanvasTooltip} from '~/common/FabricInvitation/EventCreateDesign/components/CanvasTooltip/CanvasTooltip';
import {Uyo} from '~/common/FabricInvitation/EventCreateDesign/components/CardEditor/actions/Uyo/Uyo';
import {EnvelopeDisplay} from '~/common/FabricInvitation/EventCreateDesign/components/EnvelopeDisplay';
import {Sections} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {useEnvelopeContext} from '~/common/FabricInvitation/EventCreateDesign/contexts/EnvelopeContext';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {selectIsPremium} from '~/common/FabricInvitation/reducers/fabricTemplate/selectors';
import {useUyo} from '~/common/fabric/Card/hooks/useUyo';
import {eventInEditMode} from '~/common/fabric/reducers/details/selectors';
import {
  SET_TEMPLATE_SNAPSHOT,
  UPDATE_DESIGN_OPTIONS,
} from '~/common/fabric/reducers/fabricDesign/constants';
import {
  selectActiveCardEditor,
  selectCountOfUyoImages,
  selectCurrentSection,
  selectDesignHasLogo,
  selectFabricArt,
  selectOriginalFabricArt,
  selectPristineUyo,
  selectShowMatchingSetPreview,
  selectTouched,
  selectUsedPremiumFeature,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {
  findActiveTextbox,
  getLogoObjectIds,
  getUyoObjectIds,
} from '~/common/fabric/utils/objectFinders';
import {useCachedMedia} from '~/common/hooks/useCachedMedia';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {
  GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME,
  GALLERY_PHOTO_PREVIEW_MEDIA_ID,
} from '~/common/indexed-db/dexieStores';
import {useOrientation} from '../hooks/useOrientation';
import {useEnvelopeNavigation} from './EnvelopeDisplay/hooks/useEnvelopeNavigation';
import './CardTemplate.sass';

const removeElements = (objects) =>
  objects?.map((obj) => omit(obj, ['_originalText', '_originalObject', 'version', 'styles']));

const UyoElementWrapper = ({btnText, btnSize, isIn = false, nodeRef = null, uyoId, index}) => (
  <CSSTransition
    in={isIn}
    nodeRef={nodeRef}
    timeout={0}
    classNames={{
      enter: 'animate__animated animate__fadeIn',
      enterActive: 'animate__animated animate__fadeOut',
      enterDone: 'animate__animated animate__fadeOut',
      exit: 'animate__animated animate__fadeOut',
      exitActive: 'animate__animated animate__fadeIn',
      exitDone: 'animate__animated animate__fadeIn',
    }}
    key={uyoId || index}
  >
    <Uyo btnText={btnText} btnSize={btnSize} nodeRef={nodeRef} index={index} id={uyoId} />
  </CSSTransition>
);

export const CardTemplate = ({isCurrentSectionInEnvelope}) => {
  const envelopeRef = useRef(null);
  const elementRef = useRef(null);
  const dispatch = useDispatch();
  const currentRoute = useCurrentRoute();

  const fabricArt = useSelector(selectFabricArt);
  const originalFabricArt = useSelector(selectOriginalFabricArt);
  const touched = useSelector(selectTouched);
  const designHasLogo = useSelector(selectDesignHasLogo);
  const pristineUyo = useSelector(selectPristineUyo);
  const countOfUyoImages = useSelector(selectCountOfUyoImages);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const showMatchingSetPreview = useSelector(selectShowMatchingSetPreview);
  const activeCardEditor = useSelector(selectActiveCardEditor);
  const isPremium = useSelector(selectIsPremium);
  const inEditMode = useSelector(eventInEditMode);

  const {canvas, setCanvas, setActiveObjects} = useContext(EventCreateContext);
  const {position} = useEnvelopeContext();
  const currentSection = useSelector(selectCurrentSection);
  const {getCachedMediaDataById} = useCachedMedia(GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME);
  const isDesktop = useIsLargishAndUp();
  const inStep4RefreshVariation = isInStep4RefreshVariation(isDesktop, true);

  const isMobile = !isDesktop;
  const autoSelectFirstText = isDesktop;
  const isUyo = countOfUyoImages > 0;
  const uyoObjectIds = getUyoObjectIds(originalFabricArt);
  const logoObjectIds = getLogoObjectIds(originalFabricArt);

  const {uyoRefs, logoRefs} = useUyo();

  const media = getCachedMediaDataById(GALLERY_PHOTO_PREVIEW_MEDIA_ID);
  const hasGalleryImg = media?.length > 0;

  const templateOrientation = useOrientation();

  // NOTES: We only want to navigate to card design section when we are in the envelope section
  const {handleNavigation} = useEnvelopeNavigation(
    isMobile,
    isCurrentSectionInEnvelope ? Sections.CARD_DESIGN : null
  );

  // TODO: temporary, needs to improve implementation <-- Seems not temporary
  window.canvas = canvas;

  const saveSnapshot = useCallback(
    (snapshot) => {
      dispatch({type: SET_TEMPLATE_SNAPSHOT, payload: snapshot});
    },
    [dispatch]
  );

  const setActiveCanvasObject = useCallback(
    (canvasInstance, checkForActiveTextbox, checkForEmptyImgSrc) => {
      const canvasObjects = canvasInstance?.getObjects();

      if (activeCardEditor === 'text' && checkForActiveTextbox) {
        const textbox = findActiveTextbox(canvasObjects);

        if (textbox) {
          canvasHideTextBorder(textbox, canvasInstance);
          canvasHideEditIcon(textbox, canvasInstance);
          canvasInstance.setActiveObject(textbox);
          canvasInstance.renderAll();
        }
      } else if (activeCardEditor === 'image' && !pristineUyo) {
        const uyoImage = canvasObjects?.find((obj) => obj.type === 'uYOImage');
        canvasShowTextBordersAndIcons(canvasInstance);
        if (checkForEmptyImgSrc && uyoImage) {
          canvasInstance.setActiveObject(uyoImage);
          canvasInstance.renderAll();
        }
      }
    },
    [activeCardEditor, pristineUyo]
  );

  const onCanvasLoadHandler = useCallback(
    (canvasInstance) => {
      const containsUyo = canvasInstance?.getObjects()?.some((el) => el.type === 'uYOImage');
      if (autoSelectFirstText && isDesktop && !containsUyo) {
        dispatch({type: UPDATE_DESIGN_OPTIONS, payload: {activeCardEditor: 'text'}});
      }

      canvasRemoveTextBordersAndIcons(canvasInstance);
      dispatch({type: UPDATE_DESIGN_OPTIONS, payload: {loaded: true}});

      const uyoImage = canvasInstance.getObjects().find((obj) => obj.type === 'uYOImage');
      if (isMobile) {
        canvasAddTextBordersAndIcons(canvasInstance, originalFabricArt);
      }
      setActiveCanvasObject(canvasInstance, autoSelectFirstText, uyoImage?.src !== '');
    },
    [autoSelectFirstText, dispatch, isDesktop, originalFabricArt, setActiveCanvasObject, isMobile]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    const activeObjs = canvas?.getActiveObjects();
    const noActiveTextbox =
      !activeObjs || activeObjs.length === 0 || activeObjs[0]?.type !== 'textbox';
    setActiveCanvasObject(canvas, noActiveTextbox, autoSelectFirstText);
  }, [activeCardEditor, autoSelectFirstText, canvas, setActiveCanvasObject]);

  const onChangeHandler = useCallback(
    (newFabricArt) => {
      const objectTextFilter = (object) => object.type === 'textbox';
      const modifiedObjects = removeElements(newFabricArt.objects.filter(objectTextFilter));
      const originalObjects = removeElements(originalFabricArt.objects.filter(objectTextFilter));

      const objectsMatchOriginals = () => {
        let objectsMatch = true;

        modifiedObjects.forEach((obj) => {
          const matchFound = originalObjects.find((o) => o.text === obj.text);
          if (!matchFound) {
            objectsMatch = false;
          }
        });

        return objectsMatch;
      };

      const pristine = objectsMatchOriginals();

      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          fabricArt: newFabricArt,
          pristine,
        },
      });
    },
    [dispatch, originalFabricArt.objects]
  );

  useEffect(() => {
    // Hide or show border and edit icon based on active element
    const mouseDown = () => {
      canvasShowTextBordersAndIcons(canvas);

      canvas.getActiveObjects().forEach((obj) => {
        if (obj.type === 'textbox') {
          canvasHideTextBorder(obj, canvas);
        }
      });
    };
    const mouseUp = () => {
      canvas.getActiveObjects().forEach((activeObj) => {
        canvasHideTextBorder(activeObj, canvas);
        canvasHideEditIcon(activeObj, canvas);
      });
    };
    const objectMoving = ({target}) => {
      canvasHideTextBorder(target, target.canvas);
      canvasHideEditIcon(target, target.canvas);
    };

    const objectModified = (e) => {
      if (e.target?.type === 'textbox') {
        canvasRemoveEditIcon(e.target, canvas);
      }
    };

    canvas?.on('mouse:down', mouseDown);
    canvas?.on('mouse:up', mouseUp);
    canvas?.on('object:moving', objectMoving);
    canvas?.on('object:modified', objectModified);

    return () => {
      canvas?.off('mouse:down', mouseDown);
      canvas?.off('mouse:up', mouseUp);
      canvas?.off('object:moving', objectMoving);
      canvas?.off('object:modified', objectModified);
    };
  }, [canvas]);

  const setActiveObjectsHandler = useCallback(
    (activeObjects) => {
      let activeEditor = '';

      const payload = {
        editorDisabled: !activeObjects,
      };

      if (activeObjects?.length > 0) {
        if (activeObjects[0].type === 'textbox') {
          activeEditor = 'text';
          payload.currentSection = 'card_design';
        } else if (activeObjects[0].type === 'uYOImage') {
          activeEditor = 'image';
          payload.currentSection = 'photo_upload';
        }
      }

      if (activeEditor !== '') {
        payload.activeCardEditor = activeEditor;
      }

      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload,
      });

      setActiveObjects(activeObjects);
    },
    [dispatch, setActiveObjects]
  );

  const updateActiveCardEditorHandler = useCallback(() => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {
        activeCardEditor: 'intro',
      },
    });
  }, [dispatch]);

  const hasAppliedGalleryPhoto = useRef(false);

  useEffect(() => {
    if (!isUyo || !hasGalleryImg) {
      hasAppliedGalleryPhoto.current = false;
    }

    if (
      !isUyo ||
      !hasGalleryImg ||
      !canvas ||
      hasAppliedGalleryPhoto.current ||
      designHasLogo ||
      inEditMode
    ) {
      return;
    }

    // Use a short timeout to ensure canvas is ready
    const timeoutId = setTimeout(() => {
      const uyoImages = canvas.getObjects().filter((obj) => obj.type === 'uYOImage');

      if (uyoImages.length === 0) return;

      const hasExistingImage = uyoImages.some((img) => {
        const imgSrc = img.src || img._element?.src || '';
        return imgSrc && imgSrc !== '' && !imgSrc.includes('placeholder');
      });

      if (!hasExistingImage) {
        dispatch({type: UPDATE_DESIGN_OPTIONS, payload: {shouldUseCachedGalleryPhoto: true}});
      }

      hasAppliedGalleryPhoto.current = true;
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [hasGalleryImg, isUyo, inEditMode, canvas, designHasLogo, dispatch]);

  const showEnvelope = useMemo(
    () => (usedPremiumFeature || isPremium) && !isCurrentSectionInEnvelope,
    [usedPremiumFeature, isPremium, isCurrentSectionInEnvelope]
  );

  function handleEnvelopeNavigation() {
    if (handleNavigation) {
      if (isCurrentSectionInEnvelope) {
        const firstTextBox = canvas
          ?.getObjects()
          .find((canvasObjs) => canvasObjs.type === 'textbox');
        canvas?.setActiveObject(firstTextBox);
      }
      handleNavigation();
    }
  }

  const discardActiveBox = useCallback(() => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject?.type === 'textbox' || activeObject?.type === 'uYOImage') {
      canvas?.discardActiveObject();
      canvas?.requestRenderAll();
    }
  }, [canvas]);

  const addTextboxBorder = useCallback(() => {
    const activeObject = canvas?.getActiveObject();
    if (activeObject?.type === 'textbox') {
      canvasAddTextBordersAndIcons(canvas, originalFabricArt);
      canvas?.requestRenderAll();
    }
  }, [canvas, originalFabricArt]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: bad deps
  useEffect(() => {
    if (currentSection === Sections.CARD_DESIGN) {
      addTextboxBorder();
    } else {
      discardActiveBox();
      canvasRemoveTextBordersAndIcons(canvas);
    }
  }, [currentSection, discardActiveBox, addTextboxBorder]);

  return (
    <div
      ref={elementRef}
      className={cx(`card-template card-template__orientation--${templateOrientation}`, {
        'card-template__orientation': isCurrentSectionInEnvelope,
        'card-template--no-editor': !touched && !isUyo,
        'card-template--uyo': isUyo,
        'card-template--behind-envelope': position === 'in-front-of-card',
        'card-template--in-front-of-envelope': position === 'behind-card',
      })}
      onClick={handleEnvelopeNavigation}
      role="button"
      tabIndex={0}
    >
      <div className="card-template__images">
        {showEnvelope && (
          <CSSTransition
            in={isPremium || usedPremiumFeature || showMatchingSetPreview}
            timeout={{appear: 500}}
            nodeRef={envelopeRef}
            classNames="animated-slide-envelope"
            appear
          >
            <div
              ref={envelopeRef}
              className={`card-template__envelope card-template__envelope--${templateOrientation}`}
            >
              <EnvelopeDisplay
                type="blank"
                className={`card-template__envelope card-template__envelope--${templateOrientation}`}
                redirectTo={Sections.ENVELOPE_LINER}
                showLiner
              />
            </div>
          </CSSTransition>
        )}
        <div className={`card-template__canvas--${templateOrientation}`}>
          {uyoObjectIds.map((uyoId, index) => (
            <UyoElementWrapper
              key={uyoId || index}
              btnText="Upload Photo"
              isIn={showMatchingSetPreview}
              nodeRef={uyoRefs.current[index]}
              uyoId={uyoId}
              index={index}
            />
          ))}
          {logoObjectIds.map((logoId, index) => (
            <UyoElementWrapper
              key={logoId || index}
              btnText="Upload Logo"
              btnSize="small"
              isIn={showMatchingSetPreview}
              nodeRef={logoRefs.current[index]}
              uyoId={logoId}
              index={index}
            />
          ))}
          <Canvas
            configJson={fabricArt}
            onChange={onChangeHandler}
            setCanvas={setCanvas}
            setActiveObjects={setActiveObjectsHandler}
            saveSnapshot={saveSnapshot}
            onLoad={onCanvasLoadHandler}
            className="fabric_user_card card-template__canvas_mobile"
            parentNode={cx({
              'create-container': isMobile,
            })}
            callback={updateActiveCardEditorHandler}
          />
        </div>
        <CanvasTooltip
          fabricArt={fabricArt}
          originalFabricArt={originalFabricArt}
          isStep4={currentRoute?.name === 'preview' || currentRoute?.name === 'edit'}
          inStep4Variant={inStep4RefreshVariation}
        />
      </div>
    </div>
  );
};
